import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import Swal from "sweetalert2";
import Loader from "./components/loader";
import { alpha, styled } from "@mui/material/styles";
import {
  Container,
  Card,
  CardContent,
  makeStyles,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Box,
  Typography,
} from "@material-ui/core";
import QrReader from "react-qr-reader";
import QRCode from "qrcode";
import axios from "axios";
import { CSVDownload } from "react-csv";
import Navigation from "./Navigation";
import PropTypes from "prop-types";
import { Modal } from "@mui/material";

// How long (ms) the camera stays "closed" after a successful webcam scan.
// The door-closing CSS animation below is a 5s timeline keyed to this value —
// if you change this, update the animation's percentage keyframes to match.
const CAMERA_PAUSE_MS = 5000;

// ── Auto-fit text ──
// Shrinks font-size (down to a floor) so a single line of text never wraps
// or overflows its container. Used for Name / secondary line on the printed
// badge card, since attendee names and mobile/email vary wildly in
// length but must always sit on one line next to the QR code.
//
// It measures the rendered text with a ref and steps the font-size down 1px
// at a time until it fits — but never below `minFontSize`, so text never
// becomes unreadably small. If, even at `minFontSize`, the text still can't
// fit on a single line, it stops shrinking and instead wraps onto as many
// lines as it needs. The full text is always shown — nothing is ever cut
// off with an ellipsis. The block is allowed to grow taller when it wraps;
// the surrounding layout keeps it vertically centered against the QR code
// regardless of how many lines it ends up taking.
const FitText = ({
  text,
  maxFontSize = 20,
  minFontSize = 12,
  fontWeight = 400,
  color = "#000",
  style = {},
}) => {
  const containerRef = useRef(null);
  const [renderStyle, setRenderStyle] = useState({
    fontSize: maxFontSize,
    wrapped: false,
  });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Phase 1: measure as a single line (no wrapping), shrinking the
    // font-size step by step until it fits or we hit the floor.
    el.style.fontSize = `${maxFontSize}px`;
    el.style.whiteSpace = "nowrap";
    el.style.overflow = "hidden";

    let size = maxFontSize;
    el.style.fontSize = `${size}px`;

    while (el.scrollWidth > el.clientWidth && size > minFontSize) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }

    // Phase 2: even at the smallest allowed font size, does a single line
    // still overflow? If so, don't shrink further and don't truncate —
    // wrap onto multiple lines instead, showing the full text.
    const stillOverflowsAtFloor =
      size === minFontSize && el.scrollWidth > el.clientWidth;

    setRenderStyle({ fontSize: size, wrapped: stillOverflowsAtFloor });
  }, [text, maxFontSize, minFontSize]);

  const { fontSize, wrapped } = renderStyle;

  // Single line: nowrap, no clipping (this is only reached once the text
  // is confirmed to fit, so nothing is ever hidden here).
  // Wrapped: text flows onto as many lines as needed, fully visible, no
  // height cap and no ellipsis.
  const overflowStyle = wrapped
    ? {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }
    : {
        whiteSpace: "nowrap",
      };

  return (
    <div
      ref={containerRef}
      style={{
        fontSize: `${fontSize}px`,
        fontWeight,
        color,
        lineHeight: 1.25,
        ...overflowStyle,
        ...style,
      }}
    >
      {text}
    </div>
  );
};

const ScanQr = (props) => {
  const isDisabled = props.isSelfCheckIn || false;
  const [scanResultFile, setScanResultFile] = useState("");
  const [scanResultWebCam, setScanResultWebCam] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [uid, setUid] = useState("");

  // ── Search (by Registration Number — the unique identifier) ──
  const [searchRegistrationNumber, setSearchRegistrationNumber] = useState("");

  // ── Active form fields ──
  const [Firstname, setFirstName] = useState("");
  const [RegistrationNumber, setRegistrationNumber] = useState("");
  const [MobileNo, setMobileNo] = useState("");
  const [Email, setEmail] = useState("");
  const [Color, setColor] = useState("");
  const [Day, setDay] = useState("");
  const [Category, setCategory] = useState("");
  const [qrCode, setQrCode] = useState("");

  const [ticketId, setTicketId] = useState("");
  const [exhibitorId, setField2] = useState("");
  const [checkIn, setcheckIn] = useState(true);
  const [checkOut, setcheckOut] = useState(true);
  const [exhibitor, setExhibitor] = useState(
    JSON.parse(localStorage.getItem("token")).token
  );
  const [loading, setLoading] = useState(false);
  const [whatsappTool, setwhatsappTool] = useState("aisensy");
  const [printOpen, setPrintOpen] = useState(false);
  const classes = useStyles();
  const qrRef = useRef(null);

  // ── Camera pause state (disables the scanner for a few seconds after a
  // successful scan, with an animated "shutter doors" overlay in its place) ──
  const [cameraActive, setCameraActive] = useState(true);
  const cameraTimeoutRef = useRef(null);

  // ── "QR scanned successfully" toast popup state (auto-hides after 3s) ──
  const [scanSuccessPopup, setScanSuccessPopup] = useState({
    open: false,
    name: "",
  });
  const popupTimeoutRef = useRef(null);

  // Clean up any pending timers if the component unmounts mid-animation
  useEffect(() => {
    return () => {
      if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);
      if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    };
  }, []);

  // Shows the "QR Scanned Successfully" toast for 3 seconds, and — when
  // pauseCamera is true (i.e. a live webcam scan) — also plays the shutter
  // doors animation over the camera feed for CAMERA_PAUSE_MS, so the same
  // code isn't scanned repeatedly.
  const triggerScanSuccessFeedback = (name, pauseCamera = false) => {
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    setScanSuccessPopup({ open: true, name: name || "" });
    popupTimeoutRef.current = setTimeout(() => {
      setScanSuccessPopup({ open: false, name: "" });
    }, 3000);

    if (pauseCamera) {
      if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current);
      setCameraActive(false);
      cameraTimeoutRef.current = setTimeout(() => {
        setCameraActive(true);
      }, CAMERA_PAUSE_MS);
    }
  };

  const closeScanSuccessPopup = () => {
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
    setScanSuccessPopup({ open: false, name: "" });
  };

  if (!exhibitor) {
    localStorage.removeItem("token");
    window.location.href = "/";
    alert("Logged out");
  }

  const getQrCodeDetails = async (ticketIDLocal) => {
    let ticketDetails;
    await axios
      .get(
        process.env.REACT_APP_ADMIN + "/qrCode/getOne?ticketId=" + ticketIDLocal
      )
      .then((data) => {
        ticketDetails = data.data;
      })
      .catch((error) => console.log(error));
    return ticketDetails;
  };

  const saveQrCodeDetails = async (type) => {
    let ticketDetails = {
      uid,
      ticketId,
      Firstname,
      RegistrationNumber,
      MobileNo,
      Email,
      Color,
      Day,
      Category,
      qrCode,
      exhibitor,
      checkIn,
      type,
      whatsappTool,
    };

    if (type === "dataupdateonly") {
      const { isConfirmed } = await Swal.fire({
        title: "Confirm Update",
        html: "Only data will be updated. No badge will be resent.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, update",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#e81b23",
        customClass: { popup: "custom-swal-popup" },
      });
      if (!isConfirmed) { setLoading(false); return; }
    }

    if (type === "DataUpdate") {
      const { isConfirmed } = await Swal.fire({
        title: "Confirm Update",
        html: "A whatsapp message will be resent to the attendee, reflecting the updated seat assignment.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, update",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#e81b23",
        customClass: { popup: "custom-swal-popup" },
      });
      if (!isConfirmed) { setLoading(false); return; }
    }

    if (type === "onSpot") {
      const { isConfirmed } = await Swal.fire({
        title: "Confirm Update",
        html: "New attendee details will be added, the check-in will be recorded, and the badge will be sent.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, update",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#e81b23",
        customClass: { popup: "custom-swal-popup" },
      });
      if (!isConfirmed) { setLoading(false); return; }
    }

    setLoading(!loading);

    await axios
      .post(process.env.REACT_APP_ADMIN + "/qrCode/saveOne", ticketDetails)
      .then((data) => {
        ticketDetails = data.data;
        setLoading(false);
        Swal.fire({
          title: "Success",
          html: `${data.data.message}`,
          icon: "success",
          confirmButtonText: "Close",
          confirmButtonColor: "#e81b23",
          customClass: { popup: "custom-swal-popup" },
        }).then((result) => {
          if (result.isConfirmed) window.location.reload();
        });
        setTimeout(() => { window.location.reload(); }, 2500);
      })
      .catch((error) => console.log(error));
    setLoading(false);

    return ticketDetails;
  };

  function bufferToImageUrl(bufferObj) {
    if (!bufferObj || !bufferObj.data) {
      console.error("Invalid buffer object:", bufferObj);
      return null;
    }
    const uint8Array = new Uint8Array(bufferObj.data);
    const blob = new Blob([uint8Array], { type: "image/png" });
    return URL.createObjectURL(blob);
  }

  const checkOutUser = async () => {
    let ticketDetails = { uid, ticketId, exhibitorId, checkOut };
    await axios
      .post(process.env.REACT_APP_ADMIN + "/qrCode/checkout", ticketDetails)
      .then((data) => {
        ticketDetails = data.data;
        alert(data.data.message);
        window.location.reload();
      })
      .catch((error) => console.log(error));
    return ticketDetails;
  };

  const printLabel = async () => {
    let ticketDetails = { uid, Firstname };
    try {
      const response = await axios.post(
        process.env.REACT_APP_ADMIN + "/qrCode/printlabel",
        ticketDetails
      );
      const imageUrl = bufferToImageUrl(response.data.buffer);
      if (imageUrl) {
        const iframe = document.createElement("iframe");
        iframe.style.position = "absolute";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "none";
        document.body.appendChild(iframe);
        iframe.onload = function () {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        };
        iframe.srcdoc = `<html><body style="margin:0;"><img src="${imageUrl}" style="width:100%;"/></body></html>`;
      }
    } catch (error) {
      console.error("Error printing:", error);
    }
  };

  // Opens the in-app print preview popup (styled like the app) showing
  // Name, secondary line, and QR code, laid out exactly the same way the
  // actual printed card will be. The actual print is triggered from the
  // "Print" button inside that popup.
  const openPrintPreview = () => {
    if (!qrCode) {
      alert("No QR code available to print");
      return;
    }
    setPrintOpen(true);
  };

  const closePrintPreview = () => setPrintOpen(false);

  // Triggers the browser print dialog, then closes the preview popup once
  // the dialog has been dismissed (window.print() blocks script execution
  // until the print dialog is closed in all major browsers).
  const handlePrintNow = () => {
    window.print();
    closePrintPreview();
  };

  const searchByRegistrationNumber = async (RegistrationNumberQuery) => {
    let ticketDetails;
    await axios
      .get(
        process.env.REACT_APP_ADMIN +
          "/qrCode/getByRegistrationNumber?RegistrationNumber=" +
          RegistrationNumberQuery
      )
      .then((data) => { ticketDetails = data.data; })
      .catch((error) => console.log(error));
    return ticketDetails;
  };

  const sendBadge = async () => {
    try {
      const { value: confirmed } = await Swal.fire({
        title: "Are you sure?",
        text: "Only the badge will be resent — no changes will be made to the existing data",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, send it!",
        confirmButtonColor: "#e81b23",
        cancelButtonText: "Cancel",
        customClass: { popup: "custom-swal-popup" },
      });
      if (!confirmed) { setLoading(false); return; }
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_ADMIN}/qrCode/sendBadge`,
        null,
        { params: { ticketId: uid, whatsappTool } }
      );

      const ticketDetails = response.data;
      setLoading(false);

      if (ticketDetails.result || ticketDetails.success) {
        await Swal.fire({
          title: "Success",
          html: "Badge Sent Successfully",
          icon: "success",
          confirmButtonText: "Close",
          confirmButtonColor: "#e81b23",
          customClass: { popup: "custom-swal-popup" },
        });
        window.location.reload();
      } else {
        Swal.fire({ title: "Error", html: "Failed to send badge.", icon: "error", confirmButtonText: "Close", confirmButtonColor: "#e81b23" });
      }
    } catch (error) {
      console.error("Error sending badge:", error);
      Swal.fire({ title: "Error", html: "Something went wrong while sending the badge.", icon: "error", confirmButtonText: "Close", confirmButtonColor: "#e81b23" });
    }
  };

  const handleDownloadCsv = async () => {
    await axios
      .get(process.env.REACT_APP_ADMIN + "/qrCode/getAllScannedUsers?exhibitor=" + exhibitor)
      .then((data) => { setCsvData(data.data); })
      .catch((error) => console.log(error));
  };

  const populateFields = (ticketDetails) => {
    if (!ticketDetails) return;
    setTicketId(ticketDetails?.ticketId);
    setUid(ticketDetails?._id);
    setFirstName(ticketDetails?.Firstname || "");
    setRegistrationNumber(ticketDetails?.RegistrationNumber || "");
    setMobileNo(ticketDetails?.MobileNo || "");
    setEmail(ticketDetails?.Email || "");
    setColor(ticketDetails?.Color || "");
    setColor(ticketDetails?.Color || "");
    setDay(ticketDetails?.Day || "");
    setCategory(ticketDetails?.Category || "");
    setQrCode(ticketDetails?.qrCode || ticketDetails?.ticketId || "");
    setField2(exhibitor);
  };

  const handleErrorFile = (error) => { console.log(error); };

  const handleScanFile = async (result) => {
    if (result) {
      setScanResultFile(result);
      const ticketDetails = await getQrCodeDetails(result);
      console.log("sssss", ticketDetails);
      if (ticketDetails) {
        populateFields(ticketDetails);
        // File-based scan: show the success toast, no need to play the
        // shutter-door animation since there isn't a live feed here.
        triggerScanSuccessFeedback(ticketDetails?.Firstname, false);
      }
    }
  };

  const onScanFile = () => { qrRef.current.openImageDialog(); };

  const handleErrorWebCam = async (error) => { console.log(error); };

  const handleScanWebCam = async (result) => {
    // Ignore scans while the camera is intentionally "closed" after a
    // previous successful read.
    if (!cameraActive) return;

    if (result) {
      setScanResultWebCam(result);
      const ticketDetails = await getQrCodeDetails(result);
      console.log("sssss", ticketDetails);
      if (ticketDetails) {
        populateFields(ticketDetails);
        // Live webcam scan: show the success toast AND play the shutter
        // doors animation over the camera for CAMERA_PAUSE_MS.
        triggerScanSuccessFeedback(ticketDetails?.Firstname, true);
      }
    }
  };

  const handleSearch = async (RegistrationNumberQuery) => {
    if (RegistrationNumberQuery) {
      const ticketDetails = await searchByRegistrationNumber(RegistrationNumberQuery);
      console.log("search", RegistrationNumberQuery);
      if (ticketDetails) {
        populateFields(ticketDetails);
      } else {
        alert("User not found");
      }
    }
  };

  // ── Shared print card layout ──
  // Used for BOTH the on-screen preview (inside the Print Preview popup)
  // and the actual printed output (portal target below), so what the user
  // sees in the preview is exactly what gets printed: Name + secondary
  // line (Mobile No • Email) on the left, QR code on the right.
  //
  // The text block is vertically centered against the QR code via the
  // row's `alignItems: center`. Normally the block is exactly as tall as
  // its two lines of text and sits centered next to the QR image. If a
  // very long value forces a wrap, the block simply grows taller — full
  // text is always shown, never clipped — and `alignItems: center` keeps
  // it (and the QR code beside it) centered against whichever one ends up
  // taller, so nothing gets pushed off-center.
  const secondaryLine = [MobileNo, Email].filter(Boolean).join(" • ");

  const PrintCardContent = ({ qrSize = 150 }) => (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: "16px",
      }}
    >
      <div
        style={{
          textAlign: "left",
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        <FitText
          text={Firstname}
          maxFontSize={24}
          minFontSize={14}
          fontWeight={700}
          color="#000"
        />
        <FitText
          text={secondaryLine}
          maxFontSize={16}
          minFontSize={11}
          fontWeight={400}
          color="#555"
        />
        {Color && (
          <FitText
            text={Color}
            maxFontSize={14}
            minFontSize={10}
            fontWeight={600}
            color="#e81b23"
          />
        )}
        {(Day || Category) && (
          <FitText
            text={[Day, Category].filter(Boolean).join(" • ")}
            maxFontSize={13}
            minFontSize={10}
            fontWeight={500}
            color="#777"
          />
        )}
      </div>
      {qrCode && (
        <img
          src={qrCode}
          alt="QR Code"
          width={qrSize}
          height={qrSize}
          style={{ display: "inline-block", flexShrink: 0 }}
        />
      )}
    </div>
  );

  return (
    <Container className={classes.conatiner}>
      <div className={classes.bgOrb1}></div>
      <div className={classes.bgOrb2}></div>
      <div className={classes.bgOrb3}></div>

      {loading && <Loader />}
      <Card className={classes.card}>
        <CardContent>
          <Navigation />
          <br />
          <Grid container className={classes.mainContent}>
            <Grid item xl={5} lg={5} md={6} sm={12} xs={12}>
              <div className={classes.scanSection}>
                <h3 className={classes.sectionTitle}>Scan QR Code</h3>
                <div className={classes.qrReaderWrapper}>
                  <span className={classes.cornerTL}></span>
                  <span className={classes.cornerTR}></span>
                  <span className={classes.cornerBL}></span>
                  <span className={classes.cornerBR}></span>
                  <div className={classes.scanLine}></div>

                  {/* Camera feed stays mounted at all times — scans are just
                      ignored while cameraActive is false — so the shutter
                      doors can visually close/open over a continuously
                      live feed instead of blanking out the video. */}
                  <QrReader
                    facingMode={isDisabled ? "user" : "environment"}
                    delay={300}
                    style={{ width: "100%" }}
                    onError={handleErrorWebCam}
                    onScan={handleScanWebCam}
                  />

                  {/* ── 2D camera shutter doors ── */}
                  {!cameraActive && (
                    <div className={classes.shutterOverlay}>
                      <div className={classes.shutterDoorLeft}>
                        <span className={classes.shutterSeamLeft}></span>
                      </div>
                      <div className={classes.shutterDoorRight}>
                        <span className={classes.shutterSeamRight}></span>
                      </div>
                      <div className={classes.shutterContent}>
                        <div className={classes.pausedCheckCircle}>
                          <svg
                            width="28"
                            height="28"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        </div>
                        <p className={classes.cameraPausedText}>Scan Successful</p>
                        <p className={classes.cameraPausedSubtext}>
                          Resuming camera
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Grid>

            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <div className={classes.formSection}>
                <h3 className={classes.sectionTitle}>Attendee Details</h3>

                <Grid item xl="auto" lg="auto" md="auto" sm="auto" xs="auto">
                  <div className={classes.searchWrapper}>
                    <CssTextField
                      className={classes.searchTextField}
                      label="Search by Registration Number"
                      value={searchRegistrationNumber}
                      onChange={(e) => setSearchRegistrationNumber(e.target.value)}
                    />
                    <Button
                      className={classes.searchBtn}
                      variant="contained"
                      color="primary"
                      onClick={() => handleSearch(searchRegistrationNumber)}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Grid>
                <br />

                {/* ── Active Fields (visible) ── */}
                <CssTextField
                  label="Registration Number"
                  style={{ display: "flex" }}
                  value={RegistrationNumber}
                  variant="outlined"
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                />
                <br />
                <CssTextField
                  label="Name"
                  style={{ display: "flex" }}
                  value={Firstname}
                  variant="outlined"
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <br />
                <CssTextField
                  label="Mobile No"
                  style={{ display: "flex" }}
                  value={MobileNo}
                  variant="outlined"
                  onChange={(e) => setMobileNo(e.target.value)}
                />
                <br />
                <CssTextField
                  label="Email"
                  style={{ display: "flex" }}
                  value={Email}
                  variant="outlined"
                  onChange={(e) => setEmail(e.target.value)}
                />
                <br />
                <CssTextField
                  label="Color"
                  style={{ display: "flex" }}
                  value={Color}
                  variant="outlined"
                  onChange={(e) => setColor(e.target.value)}
                />
                <br />
                <CssTextField
                  label="Day"
                  style={{ display: "flex" }}
                  value={Day}
                  variant="outlined"
                  onChange={(e) => setDay(e.target.value)}
                />
                <br />
                <CssTextField
                  label="Category"
                  style={{ display: "flex" }}
                  value={Category}
                  variant="outlined"
                  onChange={(e) => setCategory(e.target.value)}
                />
                <br />

                <CssTextField
                  label="QR Code"
                  style={{ display: "none" }}
                  value={qrCode}
                  variant="outlined"
                  onChange={(e) => setQrCode(e.target.value)}
                />
                <br />

                {/* Hidden exhibitorId field */}
                <CssTextField
                  disabled={isDisabled}
                  style={{ display: "none" }}
                  label="exhibitorId"
                  value={exhibitorId}
                  onChange={(e) => setField2(e.target.value)}
                />

                <Box style={{ display: "none" }} className={classes.switchWrapper}>
                  <Typography variant="body1" className={classes.switchLabel}>Interakt</Typography>
                  <Switch
                    checked={whatsappTool === "aisensy"}
                    onChange={(e) => setwhatsappTool(e.target.checked ? "aisensy" : "interakt")}
                    color="Primary"
                  />
                  <Typography variant="body1" className={classes.switchLabel}>Aisensy</Typography>
                </Box>

                <div className={classes.buttonGrid}>
                  <Button className={classes.btn} variant="contained" color="primary" onClick={() => saveQrCodeDetails("checkIn")}>Check In</Button>
                  <Button className={classes.btn} variant="contained" color="primary" onClick={() => saveQrCodeDetails("dataupdateonly")}>Update Data</Button>
                  <Button className={classes.btn} variant="contained" color="primary" onClick={openPrintPreview}>Print</Button>
                </div>

                {imageUrl ? (
                  <a href={imageUrl} download>
                    <img src={imageUrl} alt="img" />
                  </a>
                ) : null}
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── "QR Scanned Successfully" toast — auto-hides after 3 seconds ── */}
      <Modal
        open={scanSuccessPopup.open}
        onClose={closeScanSuccessPopup}
        hideBackdrop
        disableAutoFocus
        disableEnforceFocus
        style={{ pointerEvents: "none" }}
      >
        <Box className={classes.scanSuccessToast} style={{ pointerEvents: "auto" }}>
          <div className={classes.scanSuccessIconWrap}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <p className={classes.scanSuccessTitle}>QR Scanned Successfully</p>
          {scanSuccessPopup.name && (
            <p className={classes.scanSuccessName}>{scanSuccessPopup.name}</p>
          )}
        </Box>
      </Modal>

      {/* ── Print Preview popup: pops open from dead-center of the screen,
          layout matches the actual printed card exactly ── */}
      <Modal open={printOpen} onClose={closePrintPreview}>
        <Box className={classes.printModal}>
          <button className={classes.printCloseBtn} onClick={closePrintPreview}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <p className={classes.printTitle}>Print Preview</p>

          {/* Same PrintCardContent layout used for the actual print output below,
              so the preview is a faithful representation of what gets printed. */}
          <div
            style={{
              background: "#fff",
              color: "#000",
              borderRadius: "10px",
              padding: "15vh 20px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <PrintCardContent qrSize={120} />
          </div>

          <Button
            className={classes.btn}
            variant="contained"
            color="primary"
            style={{ width: "100%" }}
            onClick={handlePrintNow}
          >
            Print
          </Button>
        </Box>
      </Modal>

      {/*
        ── ACTUAL print target, rendered via portal directly onto document.body ──

        Why a portal:
        The Print Preview Modal's Box is positioned/animated with a `transform`.
        Any ancestor with a `transform` becomes the containing block for
        `position: fixed` descendants — so if the print target lived inside that
        Box, its fixed positioning would be computed relative to the modal's
        on-screen position, not the actual printed page. Rendering this node
        straight onto <body> via a portal avoids that transformed ancestor
        entirely.

        Layout mirrors PrintCardContent exactly (Name + secondary line left,
        QR right) so the preview and the real printout always match.
      */}
      {printOpen &&
        ReactDOM.createPortal(
          <div
            className="qr-print-target"
            style={{
              background: "#fff",
              color: "#000",
              // Fixed to the same 3.5" x 5" card size the badge is designed
              // for (matching the printer/paper this is meant for), rather
              // than "100%", so the box has a real, known size at all times —
              // see the note below on why that matters for FitText.
              width: "3.5in",
              height: "5in",
              boxSizing: "border-box",
              padding: "12.5% 15px 20px 30px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <PrintCardContent qrSize={150} />
          </div>,
          document.body,
        )}

      {/*
        Print-only styles:

        IMPORTANT — why this uses visibility/position instead of display:none:
        FitText (used inside PrintCardContent) measures its own rendered
        width via a ref (scrollWidth/clientWidth) to decide how much to
        shrink or wrap the text. An element with `display: none` has a
        width of 0, so if the print target were hidden that way, FitText
        would measure 0 and never shrink or wrap — it would just render at
        max font size and hard-clip. That's why the real printout previously
        didn't match the "Print Preview" popup (which IS visible, so it
        measures correctly).

        Instead, the target stays in the layout at all times — sized to its
        real 3.5in x 5in card dimensions — but is kept off-screen and
        invisible via `position: fixed` (removes it from the page flow) and
        `visibility: hidden` (invisible, but still measurable). During
        printing, only `visibility` flips to `visible` (and everything else
        on the page flips to `hidden`), so the exact same layout that was
        already measured on screen is what gets printed — no last-moment
        remeasurement, no mismatch with the preview.

        NOTE: Browser-injected print headers/footers (page URL, "1 of 1", date/time)
        cannot be removed from code — they come from the browser's print dialog,
        not from this page's DOM. Disable them manually in the print dialog:
          Chrome:  More settings → uncheck "Headers and footers"
          Firefox: scroll print settings → "Headers and footers" → set all six
                   dropdowns to "--blank--"
      */}
      {printOpen && (
        <style>{`
          .qr-print-target {
            visibility: hidden;
            position: fixed;
            top: 0;
            left: 0;
          }
          @media print {
            @page { size: 3.5in 5in; margin: 0; }
            html, body {
              height: 100%;
              margin: 0;
            }
            body * { visibility: hidden; }
            .qr-print-target, .qr-print-target * { visibility: visible; }
          }
        `}</style>
      )}
    </Container>
  );
};

const CssTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#e81b23",
    fontWeight: 600,
  },
  "& .MuiInputBase-input": {
    color: "#f0f0f5",
  },

  "& .MuiFormLabel-root": {
    color: "rgba(200, 200, 210, 0.7)",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#e81b23",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.07)",
    borderRadius: "10px",
    transition: "all 0.3s ease",
    "& input": {
      color: "#f0f0f5",
    },
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.12)",
    },
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      "& fieldset": {
        borderColor: "rgba(232, 27, 35, 0.5)",
      },
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(232, 27, 35, 0.06)",
      boxShadow: "0 0 0 3px rgba(232, 27, 35, 0.12)",
      "& fieldset": {
        borderColor: "#e81b23",
        borderWidth: "2px",
      },
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#e81b23",
    fontWeight: 600,
  },
});

const useStyles = makeStyles((theme) => ({
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(28px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes slideDown": {
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes orbFloat1": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "33%": { transform: "translate(60px, -80px) scale(1.15)" },
    "66%": { transform: "translate(-40px, 40px) scale(0.9)" },
  },
  "@keyframes orbFloat2": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "33%": { transform: "translate(-70px, 50px) scale(1.1)" },
    "66%": { transform: "translate(50px, -60px) scale(0.95)" },
  },
  "@keyframes orbFloat3": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)" },
    "50%": { transform: "translate(40px, 60px) scale(1.2)" },
  },
  "@keyframes scanLine": {
    "0%": { top: "4px", opacity: 0.9 },
    "49%": { opacity: 0.9 },
    "50%": { top: "calc(100% - 4px)", opacity: 0.9 },
    "100%": { top: "4px", opacity: 0.9 },
  },
  "@keyframes cornerPulse": {
    "0%, 100%": { opacity: 0.6 },
    "50%": { opacity: 1 },
  },
  "@keyframes btnShine": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },

  // ── Print popup: pops open FROM the exact center of the screen ──
  // Starts as a near-zero-scale point anchored at its own center and grows
  // outward, rather than sliding in from any direction.
  "@keyframes popFromCenter": {
    "0%": { opacity: 0, transform: "translate(-50%, -50%) scale(0.25)" },
    "60%": { opacity: 1, transform: "translate(-50%, -50%) scale(1.04)" },
    "100%": { opacity: 1, transform: "translate(-50%, -50%) scale(1)" },
  },

  // ── Camera shutter-door animation ──
  // Single 5s timeline (matches CAMERA_PAUSE_MS) shared by both door panels:
  // 0%–12%   doors slide in and meet at the center (closing)
  // 12%–88%  doors stay shut
  // 88%–100% doors slide back out (opening), revealing the live camera again
  "@keyframes shutterDoorLeft": {
    "0%": { transform: "translateX(-100%)" },
    "12%": { transform: "translateX(0%)" },
    "88%": { transform: "translateX(0%)" },
    "100%": { transform: "translateX(-100%)" },
  },
  "@keyframes shutterDoorRight": {
    "0%": { transform: "translateX(100%)" },
    "12%": { transform: "translateX(0%)" },
    "88%": { transform: "translateX(0%)" },
    "100%": { transform: "translateX(100%)" },
  },
  // The "Scan Successful" content only appears once the doors have fully
  // closed, and fades out just before they reopen.
  "@keyframes shutterContentReveal": {
    "0%": { opacity: 0, transform: "scale(0.6)" },
    "14%": { opacity: 0, transform: "scale(0.6)" },
    "20%": { opacity: 1, transform: "scale(1.08)" },
    "26%": { opacity: 1, transform: "scale(1)" },
    "84%": { opacity: 1, transform: "scale(1)" },
    "90%": { opacity: 0, transform: "scale(0.85)" },
    "100%": { opacity: 0, transform: "scale(0.85)" },
  },
  "@keyframes checkPop": {
    "0%": { transform: "scale(0)", opacity: 0 },
    "60%": { transform: "scale(1.15)", opacity: 1 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },

  // ── Toast animations ──
  "@keyframes toastSlideDown": {
    from: { opacity: 0, transform: "translate(-50%, -16px) scale(0.95)" },
    to: { opacity: 1, transform: "translate(-50%, 0) scale(1)" },
  },

  conatiner: {
    background: "linear-gradient(160deg, #0a0a0f 0%, #12121a 40%, #0d0d15 100%)",
    height: "100vh",
    width: "100vw",
    maxWidth: "100vw",
    position: "relative",
    padding: "0 !important",
    overflow: "hidden",
  },

  bgOrb1: {
    position: "fixed",
    top: "-15%",
    left: "-10%",
    width: 600,
    height: 600,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(232, 27, 35, 0.18) 0%, rgba(232, 27, 35, 0.04) 50%, transparent 70%)",
    animation: "$orbFloat1 12s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(8px)",
  },
  bgOrb2: {
    position: "fixed",
    bottom: "-20%",
    right: "-10%",
    width: 700,
    height: 700,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(180, 10, 18, 0.14) 0%, rgba(120, 0, 10, 0.05) 50%, transparent 70%)",
    animation: "$orbFloat2 16s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(12px)",
  },
  bgOrb3: {
    position: "fixed",
    top: "40%",
    left: "30%",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255, 80, 80, 0.06) 0%, transparent 70%)",
    animation: "$orbFloat3 20s ease-in-out infinite",
    pointerEvents: "none",
    zIndex: 0,
    filter: "blur(20px)",
  },

  card: {
    background: "rgba(14, 14, 20, 0.88)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    boxShadow: "0 8px 64px 0 rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.05)",
    height: "99.6%",
    margin: "0 !important",
    padding: "0 !important",
    overflowY: "scroll",
    position: "relative",
    zIndex: 1,
    animation: "$fadeIn 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255, 255, 255, 0.03)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "linear-gradient(180deg, #e81b23 0%, #7b0008 100%)",
      borderRadius: "10px",
    },
  },

  mainContent: {
    justifyContent: "space-evenly",
    padding: theme.spacing(4),
    animation: "$fadeIn 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },

  scanSection: {
    background: "linear-gradient(160deg, #111118 0%, #0c0c14 100%)",
    padding: theme.spacing(4),
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    transition: "box-shadow 0.4s ease, border-color 0.4s ease",
    animation: "$fadeIn 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
    "&:hover": {
      borderColor: "rgba(232, 27, 35, 0.35)",
      boxShadow: "0 8px 40px rgba(232, 27, 35, 0.2), inset 0 1px 0 rgba(255,255,255,0.07)",
    },
  },

  qrReaderWrapper: {
    position: "relative",
    borderRadius: "14px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 0 0 1px rgba(232,27,35,0.15), 0 8px 24px rgba(0,0,0,0.4)",
    minHeight: "260px",
  },

  scanLine: {
    position: "absolute",
    left: "6px",
    right: "6px",
    height: "2px",
    background: "linear-gradient(90deg, transparent 0%, #e81b23 30%, #ff6b6b 50%, #e81b23 70%, transparent 100%)",
    boxShadow: "0 0 10px rgba(232,27,35,0.8), 0 0 20px rgba(232,27,35,0.4)",
    borderRadius: "2px",
    zIndex: 10,
    animation: "$scanLine 2.4s linear infinite",
    pointerEvents: "none",
  },

  cornerTL: {
    position: "absolute", top: 6, left: 6, width: 20, height: 20,
    borderTop: "2.5px solid #e81b23", borderLeft: "2.5px solid #e81b23",
    borderRadius: "3px 0 0 0", zIndex: 11, pointerEvents: "none",
    animation: "$cornerPulse 2.4s ease-in-out infinite",
  },
  cornerTR: {
    position: "absolute", top: 6, right: 6, width: 20, height: 20,
    borderTop: "2.5px solid #e81b23", borderRight: "2.5px solid #e81b23",
    borderRadius: "0 3px 0 0", zIndex: 11, pointerEvents: "none",
    animation: "$cornerPulse 2.4s ease-in-out infinite 0.2s",
  },
  cornerBL: {
    position: "absolute", bottom: 6, left: 6, width: 20, height: 20,
    borderBottom: "2.5px solid #e81b23", borderLeft: "2.5px solid #e81b23",
    borderRadius: "0 0 0 3px", zIndex: 11, pointerEvents: "none",
    animation: "$cornerPulse 2.4s ease-in-out infinite 0.4s",
  },
  cornerBR: {
    position: "absolute", bottom: 6, right: 6, width: 20, height: 20,
    borderBottom: "2.5px solid #e81b23", borderRight: "2.5px solid #e81b23",
    borderRadius: "0 0 3px 0", zIndex: 11, pointerEvents: "none",
    animation: "$cornerPulse 2.4s ease-in-out infinite 0.6s",
  },

  // ── 2D camera shutter doors: overlays the live feed, closes, holds with
  // the success message, then reopens — all in one 5s timeline ──
  shutterOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 20,
    overflow: "hidden",
    pointerEvents: "auto",
  },

  shutterDoorLeft: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "50%",
    height: "100%",
    background: "linear-gradient(135deg, #1c1c26 0%, #0b0b11 100%)",
    borderRight: "1px solid rgba(232,27,35,0.35)",
    boxShadow: "inset -10px 0 24px rgba(0,0,0,0.5)",
    animation: "$shutterDoorLeft 5s linear forwards",
    display: "flex",
    justifyContent: "flex-end",
  },

  shutterDoorRight: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "50%",
    height: "100%",
    background: "linear-gradient(135deg, #1c1c26 0%, #0b0b11 100%)",
    // borderLeft: "1px solid rgba(232,27,35,0.35)",
    boxShadow: "inset 10px 0 24px rgba(0,0,0,0.5)",
    animation: "$shutterDoorRight 5s linear forwards",
  },

  shutterSeamLeft: {
    width: "2px",
    height: "100%",
  },

  shutterSeamRight: {
    width: "2px",
    height: "100%",
  },

  shutterContent: {
    position: "absolute",
    top: "35%",
    left: "31.5%",
    transform: "translate(-50%, -50%)",
    zIndex: 21,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    animation: "$shutterContentReveal 5s linear forwards",
  },

  pausedCheckCircle: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(160deg, #e81b23 0%, #7b0008 100%)",
    boxShadow: "0 4px 20px rgba(232,27,35,0.5)",
  },

  cameraPausedText: {
    color: "#f0f0f5",
    fontWeight: 700,
    fontSize: "1.05rem",
    margin: 0,
    letterSpacing: "0.02em",
  },

  cameraPausedSubtext: {
    color: "rgba(200,200,210,0.55)",
    fontSize: "0.82rem",
    margin: 0,
    letterSpacing: "0.02em",
    "&::after": {
      content: '"..."',
      display: "inline-block",
      width: "1.2em",
    },
  },

  formSection: {
    background: "linear-gradient(160deg, #111118 0%, #0c0c14 100%)",
    padding: theme.spacing(4),
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "0 4px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    transition: "box-shadow 0.4s ease, border-color 0.4s ease",
    animation: "$fadeIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards",
    "&:hover": {
      borderColor: "rgba(232, 27, 35, 0.3)",
      boxShadow: "0 8px 40px rgba(232, 27, 35, 0.15), inset 0 1px 0 rgba(255,255,255,0.07)",
    },
  },

  sectionTitle: {
    color: "#f0f0f5",
    fontSize: "1.4rem",
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    textAlign: "center",
    letterSpacing: "0.03em",
    textShadow: "0 0 20px rgba(232, 27, 35, 0.4)",
  },

  searchWrapper: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "flex-end",
  },

  searchTextField: {
    flex: 1,
  },

  searchBtn: {
    backgroundColor: "#e81b23",
    color: "#fff",
    fontWeight: 700,
    padding: "14px 20px",
    borderRadius: "10px",
    minWidth: "auto",
    boxShadow: "0 4px 16px rgba(232, 27, 35, 0.4)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&:hover": {
      backgroundColor: "#c8121a",
      transform: "translateY(-3px)",
      boxShadow: "0 8px 24px rgba(232, 27, 35, 0.55)",
    },
    "&:active": {
      transform: "translateY(-1px)",
    },
    "&:focus": {
      color: "#fff",
    },
  },

  switchWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    background: "rgba(255, 255, 255, 0.04)",
    borderRadius: "10px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    transition: "border-color 0.3s ease",
    "&:hover": {
      borderColor: "rgba(232,27,35,0.3)",
    },
  },

  switchLabel: {
    color: "rgba(200, 200, 210, 0.8)",
    fontWeight: 500,
    fontSize: "0.9rem",
  },

  buttonGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },

  btn: {
    backgroundColor: "#e81b23",
    color: "#fff",
    fontWeight: 700,
    padding: "11px 22px",
    borderRadius: "10px",
    textTransform: "none",
    fontSize: "0.9rem",
    letterSpacing: "0.02em",
    boxShadow: "0 4px 16px rgba(232, 27, 35, 0.35)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)",
      backgroundSize: "200% 100%",
      backgroundPosition: "-200% center",
      transition: "background-position 0.4s ease",
    },
    "&:hover": {
      backgroundColor: "#c8121a",
      transform: "translateY(-3px) scale(1.03)",
      boxShadow: "0 8px 28px rgba(232, 27, 35, 0.55)",
      "&::after": {
        backgroundPosition: "200% center",
      },
    },
    "&:active": {
      transform: "translateY(-1px) scale(1.01)",
    },
  },

  title: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "rgba(14,14,20,0.9)",
    color: "#fff",
    padding: 20,
    marginBottom: 10,
    boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
    animation: "$slideDown 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
  },

  // ── "QR Scanned Successfully" toast — colors matched to the app's red theme ──
  scanSuccessToast: {
    position: "fixed",
    top: "32px",
    left: "50%",
    transform: "translateX(-50%)",
    animation: "$toastSlideDown 0.35s cubic-bezier(0.22,1,0.36,1) forwards",
    background: "linear-gradient(160deg,#151016 0%,#0c0c14 100%)",
    borderRadius: "16px",
    border: "1px solid rgba(232,27,35,0.35)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(232,27,35,0.08)",
    padding: "16px 26px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    minWidth: "220px",
    zIndex: 1500,
  },

  scanSuccessIconWrap: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(160deg, #e81b23 0%, #7b0008 100%)",
    boxShadow: "0 4px 16px rgba(232,27,35,0.5)",
    marginBottom: "2px",
    animation: "$checkPop 0.45s cubic-bezier(0.22,1,0.36,1)",
  },

  scanSuccessTitle: {
    color: "#f0f0f5",
    fontWeight: 700,
    fontSize: "0.95rem",
    margin: 0,
    letterSpacing: "0.02em",
  },

  scanSuccessName: {
    color: "#ff6b6b",
    fontWeight: 600,
    fontSize: "0.85rem",
    margin: 0,
    letterSpacing: "0.02em",
  },

  // ── Print Preview modal: fixed dead-center, pops open from its own middle ──
  printModal: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transformOrigin: "center center",
    animation: "$popFromCenter 0.4s cubic-bezier(0.22,1,0.36,1) forwards",
    background: "linear-gradient(160deg,#111118 0%,#0c0c14 100%)",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    borderRadius: "20px",
    boxShadow:
      "0 24px 64px rgba(0,0,0,0.7),0 0 0 1px rgba(255,255,255,0.08),inset 0 1px 0 rgba(255,255,255,0.06)",
    border: "1px solid rgba(232,27,35,0.2)",
    padding: "1.75rem",
    width: "min(360px, 90vw)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
  },

  printCloseBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "rgba(210,210,225,0.6)",
    transition: "all 0.2s ease",
    padding: 0,
    "&:hover": {
      background: "rgba(232,27,35,0.18)",
      borderColor: "rgba(232,27,35,0.4)",
      color: "#fff",
      transform: "rotate(90deg)",
    },
  },

  printTitle: {
    color: "#fff",
    fontWeight: 700,
    fontSize: "1rem",
    margin: 0,
    letterSpacing: "0.04em",
  },
}));

ScanQr.propTypes = {
  isSelfCheckIn: PropTypes.bool,
};

export default ScanQr;